import { useEffect, useState } from "react";
import "./MyAddress.css";
import { useNavigate } from "react-router-dom";
import useAuth from "../../Features/Utils/useAuth";
import { getUserOrders, toastError } from "../../api/services";

const MyOrder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);

  const fetchOrder = async () => {
    try {
      const response = await getUserOrders(user?.id);

      if (response?.data?.success) {
        const ordersData = response?.data?.data?.orders || [];
        const total = response?.data?.data?.total || ordersData.length;

        const updatedOrders = ordersData.map((order) => {
          const ids = [...new Set(order.items.map((i) => i.productID))];

          // Calculate total items count
          const totalItems = order.items.reduce(
            (sum, item) => sum + item.quantity,
            0,
          );

          // Format date
          const formattedDate = new Date(order.date).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "short",
              year: "numeric",
            },
          );

          return {
            ...order,
            productIds: ids.length === 1 ? ids[0] : ids,
            totalItems: totalItems,
            formattedDate: formattedDate,
            displayStatus:
              order.status === "pending"
                ? "Pending"
                : order.status === "confirmed"
                ? "Delivered"
                : order.status.charAt(0).toUpperCase() +
                  order.status.slice(1),
          };
        });
        setOrders(updatedOrders);
        setTotalOrders(total);
      } else {
        toastError("Failed to fetch order details");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      toastError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [navigate, user?.id]);

  const handleViewOrder = (order) => {
    setModalLoading(true);
    setSelectedOrder(order);
    setShowModal(true);
    setTimeout(() => {
      setModalLoading(false);
    }, 300);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
    setModalLoading(false);
  };

  // Safe number conversion
  const toNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Calculate order totals
  const calculateTotals = (order) => {
    const originalTotal = toNumber(order.originalTotal);
    const totalDiscount = toNumber(order.totalDiscount);
    const shippingCharge = toNumber(order.shipping_charge);
    const platformFee = toNumber(order.platformFee);
    
    const subtotal = originalTotal - totalDiscount;
    const totalWithShipping = subtotal + shippingCharge;
    const finalTotal = totalWithShipping + platformFee;
    
    return {
      originalTotal,
      totalDiscount,
      subtotal,
      shippingCharge,
      platformFee,
      finalTotal
    };
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = orders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(orders.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Go to previous page
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Go to next page
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate page numbers
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  // Skeleton Loader Component
  const SkeletonRow = () => (
    <tr>
      <td className="d-none d-sm-table-cell">
        <div className="skeleton skeleton-text"></div>
      </td>
      <td>
        <div className="skeleton skeleton-text"></div>
      </td>
      <td>
        <div className="skeleton skeleton-text"></div>
      </td>
      <td className="d-none d-md-table-cell">
        <div className="skeleton skeleton-text"></div>
      </td>
      <td className="d-none d-md-table-cell">
        <div className="skeleton skeleton-text"></div>
      </td>
      <td>
        <div className="skeleton skeleton-text"></div>
      </td>
      <td>
        <div className="skeleton skeleton-button"></div>
      </td>
    </tr>
  );

  return (
    <div className="my_acc_page main__wrapper">
      <div className="my_acc_page_content content__page section-padding">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="step-checkout-div">
                <div className="steps activestep">
                  <div className="icondiv">
                    <i className="fas fa-shopping-cart"></i>
                  </div>

                  <h4 className="step-ttl">My Orders</h4>

                  <div className="addresslist-div before-login-top">
                    <div className="desktop_table desktop_table_fb">
                      <div className="table-responsive">
                        <table className="table table-bordered order_detail_table">
                          <thead>
                            <tr>
                              <th className="d-none d-sm-table-cell">#</th>
                              <th>Order ID</th>
                              <th>Status</th>
                              <th className="d-none d-md-table-cell">Items</th>
                              <th className="d-none d-md-table-cell">Date</th>
                              <th>Amount</th>
                              <th>Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {loading ? (
                              <>
                                <SkeletonRow />
                                <SkeletonRow />
                                <SkeletonRow />
                                <SkeletonRow />
                                <SkeletonRow />
                              </>
                            ) : currentOrders.length > 0 ? (
                              currentOrders.map((order, index) => (
                                <tr key={order.id}>
                                  <td
                                    data-label="#"
                                    className="d-none d-sm-table-cell"
                                  >
                                    {indexOfFirstItem + index + 1}
                                   </td>

                                  <td data-label="Order ID">
                                    <strong>#{order.id}</strong>
                                   </td>

                                  <td data-label="Status">
                                    <span
                                      className={`badge ${
                                        order.status === "pending"
                                          ? "bg-warning text-dark"
                                          : order.status === "confirmed"
                                          ? "bg-success"
                                          : "bg-info"
                                      }`}
                                    >
                                      {order.displayStatus}
                                    </span>
                                   </td>

                                  <td
                                    data-label="Items"
                                    className="d-none d-md-table-cell"
                                  >
                                    {order.totalItems}{" "}
                                    {order.totalItems === 1 ? "Item" : "Items"}
                                   </td>

                                  <td
                                    data-label="Date"
                                    className="d-none d-md-table-cell"
                                  >
                                    {order.formattedDate}
                                   </td>

                                  <td data-label="Amount">
                                    <strong>
                                      ₹{parseFloat(order.totalAmount).toLocaleString("en-IN")}
                                    </strong>
                                   </td>

                                  <td data-label="Action">
                                    <button
                                      className="btn-view"
                                      onClick={() => handleViewOrder(order)}
                                    >
                                      <i className="fas fa-eye"></i> View
                                    </button>
                                   </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center">
                                  <div className="no-orders">
                                    <i className="fas fa-shopping-bag"></i>
                                    <p>No orders found</p>
                                    <button 
                                      className="btn-shop-now"
                                      onClick={() => navigate("/shop")}
                                    >
                                      Start Shopping
                                    </button>
                                  </div>
                                 </td>
                               </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination Section */}
                      {!loading && orders.length > 0 && totalPages > 1 && (
                        <div className="pagination-wrapper">
                          <div className="pagination-info">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, orders.length)} of {orders.length} orders
                          </div>
                          <div className="pagination">
                            <button
                              onClick={goToPreviousPage}
                              disabled={currentPage === 1}
                              className="pagination-btn prev-next"
                            >
                              <i className="fas fa-chevron-left"></i> Previous
                            </button>
                            
                            <div className="pagination-numbers">
                              {getPageNumbers().map((page, index) => (
                                page === '...' ? (
                                  <span key={index} className="pagination-dots">...</span>
                                ) : (
                                  <button
                                    key={index}
                                    onClick={() => paginate(page)}
                                    className={`pagination-btn page-number ${currentPage === page ? 'active' : ''}`}
                                  >
                                    {page}
                                  </button>
                                )
                              ))}
                            </div>
                            
                            <button
                              onClick={goToNextPage}
                              disabled={currentPage === totalPages}
                              className="pagination-btn prev-next"
                            >
                              Next <i className="fas fa-chevron-right"></i>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-shopping-bag"></i> Order Details
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            {modalLoading ? (
              <div className="modal-loading">
                <div className="spinner"></div>
                <p>Loading order details...</p>
              </div>
            ) : (
              <div className="modal-body">
                {/* Order Info */}
                <div className="order-info-section">
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Order ID:</label>
                      <span>#{selectedOrder.id}</span>
                    </div>
                    <div className="info-item">
                      <label>Order Date:</label>
                      <span>{selectedOrder.formattedDate}</span>
                    </div>
                    <div className="info-item">
                      <label>Payment Method:</label>
                      <span>
                        {selectedOrder.payment_method === "cod" 
                          ? "Cash on Delivery" 
                          : selectedOrder.payment_method === "online"
                          ? "Online Payment"
                          : selectedOrder.payment_method}
                      </span>
                    </div>
                    <div className="info-item">
                      <label>Payment Status:</label>
                      <span className={`payment-badge ${
                        selectedOrder.payment_status === "paid" 
                          ? "paid" 
                          : "pending"
                      }`}>
                        {selectedOrder.payment_status === "paid" 
                          ? "Paid" 
                          : "Pending"}
                      </span>
                    </div>
                    <div className="info-item">
                      <label>Order Status:</label>
                      <span className={`status-badge ${
                        selectedOrder.status === "pending" 
                          ? "pending" 
                          : selectedOrder.status === "confirmed"
                          ? "delivered"
                          : selectedOrder.status
                      }`}>
                        {selectedOrder.displayStatus}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                {selectedOrder.address && (
                  <div className="address-section">
                    <h4>
                      <i className="fas fa-map-marker-alt"></i> Shipping Address
                    </h4>
                    <p>{selectedOrder.address.full_address}</p>
                  </div>
                )}

                {/* Items List */}
                <div className="items-section">
                  <h4>
                    <i className="fas fa-box"></i> Items ({selectedOrder.items?.length || 0})
                  </h4>
                  <div className="items-list">
                    {selectedOrder.items?.map((item, idx) => {
                      const itemTotal = (item.price || item.productPrice || 0) * item.quantity;
                      const itemDiscount = (item.discount_amount || item.discount || 0) * item.quantity;
                      
                      return (
                        <div className="modal-order-item" key={idx}>
                          <img
                            src={item.frontImg || item.image}
                            alt={item.productName}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/60x60?text=No+Image";
                            }}
                          />
                          <div className="item-info">
                            <h5>{item.productName}</h5>
                            <p>Quantity: {item.quantity}</p>
                            <p>Price: ₹{(item.price || item.productPrice).toLocaleString("en-IN")}</p>
                            {itemDiscount > 0 && (
                              <p className="discount-text">
                                <i className="fas fa-tag"></i> Saved: ₹{itemDiscount.toLocaleString("en-IN")}
                              </p>
                            )}
                          </div>
                          <div className="item-total">
                            ₹{itemTotal.toLocaleString("en-IN")}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="price-breakdown">
                  <h4>
                    <i className="fas fa-receipt"></i> Price Summary
                  </h4>
                  {(() => {
                    const totals = calculateTotals(selectedOrder);
                    return (
                      <>
                        <div className="price-row">
                          <span>Total MRP</span>
                          <span>₹{totals.originalTotal.toLocaleString("en-IN")}</span>
                        </div>
                        <div className="price-row discount">
                          <span>Product Discounts</span>
                          <span>-₹{totals.totalDiscount.toLocaleString("en-IN")}</span>
                        </div>
                        <div className="price-row">
                          <span>Subtotal</span>
                          <span>₹{totals.subtotal.toLocaleString("en-IN")}</span>
                        </div>
                        <div className="price-row">
                          <span>Shipping & Handling</span>
                          <span>₹{totals.shippingCharge.toLocaleString("en-IN")}</span>
                          {totals.shippingCharge === 0 && totals.subtotal > 500 && (
                            <span className="free-badge">Free Shipping</span>
                          )}
                        </div>
                        {totals.platformFee > 0 && (
                          <div className="price-row">
                            <span>Platform Fee</span>
                            <span>₹{totals.platformFee.toLocaleString("en-IN")}</span>
                          </div>
                        )}
                        <div className="price-row total">
                          <span>Total Amount</span>
                          <span>₹{totals.finalTotal.toLocaleString("en-IN")}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* COD Note */}
                {selectedOrder.payment_method === "cod" && (
                  <div className="cod-note">
                    <i className="fas fa-money-bill-wave"></i>
                    Pay ₹{(() => {
                      const totals = calculateTotals(selectedOrder);
                      return totals.finalTotal.toLocaleString("en-IN");
                    })()} in cash when your order arrives
                  </div>
                )}
              </div>
            )}

            <div className="modal-footer">
              <button className="btn-close" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          backdrop-filter: blur(5px);
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .modal-container {
          background: white;
          border-radius: 24px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          animation: slideUp 0.3s ease;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }
        
        @keyframes slideUp {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #eef2f6;
          position: sticky;
          top: 0;
          background: white;
          z-index: 10;
        }
        
        .modal-header h3 {
          margin: 0;
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .modal-close {
          background: none;
          border: none;
          font-size: 1.3rem;
          cursor: pointer;
          color: #666;
          transition: all 0.2s;
          padding: 8px;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .modal-close:hover {
          background: #f0f0f0;
          color: #333;
        }
        
        .modal-body {
          padding: 24px;
        }
        
        .modal-loading {
          text-align: center;
          padding: 60px;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #facc15;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Order Info Section */
        .order-info-section {
          background: #f8f9fa;
          padding: 16px 20px;
          border-radius: 16px;
          margin-bottom: 24px;
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }
        
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .info-item label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
        }
        
        .info-item span {
          font-size: 14px;
          font-weight: 600;
          color: #1a1c1e;
        }
        
        .payment-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          width: fit-content;
        }
        
        .payment-badge.paid {
          background: #e8f5e9;
          color: #2e7d32;
        }
        
        .payment-badge.pending {
          background: #fff3e0;
          color: #ed6c02;
        }
        
        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          width: fit-content;
        }
        
        .status-badge.pending {
          background: #fff3e0;
          color: #ed6c02;
        }
        
        .status-badge.delivered {
          background: #e8f5e9;
          color: #2e7d32;
        }
        
        /* Address Section */
        .address-section {
          background: #f0f7ff;
          padding: 16px 20px;
          border-radius: 16px;
          margin-bottom: 24px;
        }
        
        .address-section h4 {
          margin: 0 0 10px 0;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #2874f0;
        }
        
        .address-section p {
          margin: 0;
          font-size: 14px;
          color: #333;
          line-height: 1.5;
        }
        
        /* Items Section */
        .items-section {
          margin-bottom: 24px;
        }
        
        .items-section h4 {
          margin: 0 0 16px 0;
          font-size: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .items-list {
          max-height: 350px;
          overflow-y: auto;
        }
        
        .modal-order-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px;
          border: 1px solid #eef2f6;
          border-radius: 12px;
          margin-bottom: 12px;
          transition: all 0.2s;
        }
        
        .modal-order-item:hover {
          background: #fafbfe;
          border-color: #facc15;
        }
        
        .modal-order-item img {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 10px;
        }
        
        .item-info {
          flex: 1;
        }
        
        .item-info h5 {
          margin: 0 0 5px 0;
          font-size: 14px;
          font-weight: 600;
        }
        
        .item-info p {
          margin: 2px 0;
          font-size: 12px;
          color: #666;
        }
        
        .discount-text {
          color: #2e7d32 !important;
          font-weight: 500;
        }
        
        .item-total {
          font-weight: 700;
          font-size: 14px;
          color: #1a1c1e;
        }
        
        /* Price Breakdown */
        .price-breakdown {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 16px;
          margin-bottom: 20px;
        }
        
        .price-breakdown h4 {
          margin: 0 0 16px 0;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .price-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          font-size: 13px;
          color: #555;
        }
        
        .price-row.discount {
          color: #2e7d32;
        }
        
        .price-row.total {
          font-size: 16px;
          font-weight: 700;
          border-top: 2px solid #e0e0e0;
          margin-top: 8px;
          padding-top: 12px;
          color: #1a1c1e;
        }
        
        .free-badge {
          background: #e8f5e9;
          color: #2e7d32;
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 12px;
          margin-left: 8px;
        }
        
        /* COD Note */
        .cod-note {
          background: #fff3e0;
          padding: 12px 16px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: #e65100;
        }
        
        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #eef2f6;
          display: flex;
          justify-content: flex-end;
          position: sticky;
          bottom: 0;
          background: white;
        }
        
        .btn-close {
          padding: 10px 24px;
          background: #f0f0f0;
          border: none;
          border-radius: 40px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.2s;
        }
        
        .btn-close:hover {
          background: #e0e0e0;
        }
        
        /* View Button */
        .btn-view {
          background: #facc15;
          color: #1a1c1e;
          border: none;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .btn-view:hover {
          background: #e5b800;
          transform: translateY(-1px);
        }
        
        /* No Orders */
        .no-orders {
          text-align: center;
          padding: 40px;
        }
        
        .no-orders i {
          font-size: 3rem;
          color: #ccc;
          margin-bottom: 15px;
        }
        
        .no-orders p {
          font-size: 16px;
          color: #666;
          margin-bottom: 20px;
        }
        
        .btn-shop-now {
          background: #facc15;
          color: #1a1c1e;
          border: none;
          padding: 10px 24px;
          border-radius: 40px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .btn-shop-now:hover {
          background: #e5b800;
        }
        
        /* Pagination Styles */
        .pagination-wrapper {
          margin-top: 30px;
          padding: 20px;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }
        
        .pagination-info {
          text-align: center;
          font-size: 13px;
          color: #666;
          margin-bottom: 15px;
        }
        
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        
        .pagination-numbers {
          display: flex;
          gap: 5px;
          flex-wrap: wrap;
        }
        
        .pagination-btn {
          padding: 8px 14px;
          border: 1px solid #e0e0e0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s;
          color: #333;
        }
        
        .pagination-btn:hover:not(:disabled) {
          background: #facc15;
          border-color: #facc15;
          color: #1a1c1e;
          transform: translateY(-1px);
        }
        
        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .pagination-btn.active {
          background: #facc15;
          border-color: #facc15;
          color: #1a1c1e;
          font-weight: 600;
        }
        
        .prev-next {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        
        .page-number {
          min-width: 36px;
          text-align: center;
        }
        
        .pagination-dots {
          padding: 8px 4px;
          color: #999;
        }
        
        /* Skeleton Styles */
        .skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
          border-radius: 4px;
        }
        
        .skeleton-text {
          height: 16px;
          width: 100%;
        }
        
        .skeleton-button {
          height: 32px;
          width: 60px;
          border-radius: 20px;
        }
        
        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        /* Table Styles */
        .order_detail_table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .order_detail_table th,
        .order_detail_table td {
          padding: 12px;
          vertical-align: middle;
        }
        
        .order_detail_table thead th {
          background: #f8f9fa;
          font-weight: 600;
          font-size: 13px;
        }
        
        .badge {
          padding: 5px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
        }
        
        .bg-warning {
          background: #fff3e0;
          color: #ed6c02;
        }
        
        .bg-success {
          background: #e8f5e9;
          color: #2e7d32;
        }
        
        .bg-info {
          background: #e3f2fd;
          color: #1976d2;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .modal-container {
            width: 95%;
            max-height: 85vh;
          }
          
          .info-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          
          .modal-order-item {
            flex-wrap: wrap;
          }
          
          .item-total {
            width: 100%;
            text-align: right;
            padding-top: 5px;
          }
          
          .order_detail_table td {
            display: block;
            text-align: right;
            padding-left: 50%;
            position: relative;
          }
          
          .order_detail_table td::before {
            content: attr(data-label);
            position: absolute;
            left: 12px;
            width: 45%;
            text-align: left;
            font-weight: 600;
            font-size: 12px;
          }
          
          .pagination {
            gap: 5px;
          }
          
          .pagination-btn {
            padding: 6px 10px;
            font-size: 11px;
          }
          
          .prev-next {
            padding: 6px 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default MyOrder;